<script setup lang="ts">
import { defineExpose } from 'vue';
import {
  iKeyIcon,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
} from '@inscribe/signal';
import { CustomerInsightTransaction } from '@/utils/customerInsights';
import {
  default as ExpandableContainer,
  ExpandableContainerSize,
} from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import { toCurrency } from '@/utils/toCurrency';
import { useRouter } from 'vue-router';
import { getWebBaseUrl } from '@/environment';

/** Refs */
const router = useRouter();

/** Props */
const props = defineProps({
  transactions: {
    type: Array<CustomerInsightTransaction>,
    required: true,
  },
});

/** Helpers */
const getTransactionTitle = (transaction: CustomerInsightTransaction) => {
  const amount = toCurrency(transaction.amount, transaction.currency_code);
  return `${amount} • ${transaction.description}`;
};

const getTransactionSubtitle = (transaction: CustomerInsightTransaction) => {
  return `${transaction.date}${transaction.category ? ` • ${transaction.category}` : ''}`;
};

/** Handlers */
const onTransactionClick = (transaction: CustomerInsightTransaction) => {
  window.open(
    `${getWebBaseUrl()}/#/customer/${router?.currentRoute?.value?.params?.customer_id}/credit-transactions`,
    '_blank',
  );
};

/** Expose */
defineExpose({
  onTransactionClick,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
  ExpandableContainerSize,
});
</script>

<template>
  <expandable-container
    title="Matching Transactions"
    :expandable="false"
    :size="ExpandableContainerSize.SMALL"
    class="signal-transactions-list"
  >
    <!-- chart -->
    <template #items>
      <div class="transactions-list d-flex flex-column">
        <expandable-container-item
          v-for="transaction in transactions"
          :key="transaction.id"
          :title="getTransactionTitle(transaction)"
          :subtitle="getTransactionSubtitle(transaction)"
          aria-label="Expandable Container Item: Fraud Signal Transaction"
          data-dd-action-name="expandable-item-fraud-transaction"
          right-icon="medium-link-square-1"
          @on-item-click="onTransactionClick"
          @on-right-icon-click="onTransactionClick"
        >
          <template #left-container>
            <i-key-icon
              icon="medium_money_coins"
              :size="iKeyIconSize.MEDIUM"
              :shape="iKeyIconShape.SQUARE"
              :icon-style="iKeyIconStyle.OUTLINE"
              :type="iKeyIconType.NEUTRAL"
            />
          </template>
        </expandable-container-item>
      </div>
    </template>
  </expandable-container>
</template>

<style lang="scss" scoped>
.signal-transactions-list {
  .transactions-list {
    gap: 1px;
    background: var(--surface-neutral);

    .expandable-item {
      width: 100%;
    }
  }
}
</style>
